import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import {
    Card,
    CardHeader,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Container,
    Row,
    Col,
    Spinner,
} from "reactstrap";
import CreatableSelect from "react-select/creatable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEdit,
    faPlusCircle,
    faTrash,
    faExclamationTriangle,
    faUndo,
    faSearch
} from "@fortawesome/free-solid-svg-icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import projectRedirectionUrlsService from "../../services/api/redirection-urls-service";
import projectsService from "../../services/api/projects-service";
import ProjectContext from '../../ProjectContext'
import ReactSelect from "react-select";

// const urlRegex = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\-]*)*$/;

const colors = {
    background: '#F4F6F9',
    cardBackground: '#FFFFFF',
    secondary: '#6C757D',
    success: '#48BB78',
    error: '#E53E3E',
    accent: '#667EEA',
    text: {
        dark: '#2D3748',
        light: '#718096'
    }
};

const RedirectionUrls = (props) => {
    const projectDetails = useContext(ProjectContext);
    const history = useHistory();

    if (projectDetails.id === '') {
        history.push("/project/allprojects");
    }
    const projectId = projectDetails.id;

    let getProjectSlug = new URLSearchParams(props.location.search).get('project');
    if (!getProjectSlug) {
        history.push({
            search: '?project=' + projectDetails.slug
        })
    }

    const [urls, setUrls] = useState([]);
    const [domain, setDomain] = useState('');
    const [url, setUrl] = useState({
        source_url: "",
        destination_url: "",
        status_code: "",
    });
    const [isEdit, setIsEdit] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [toDeleteIndex, setToDeleteIndex] = useState(null);
    const [loading, setLoading] = useState(true);

    const [statusCodeOptions, setStatusCodeOptions] = useState([
        { label: "301", value: "301" },
        { label: "302", value: "302" },
        { label: "303", value: "303" },
        { label: "307", value: "307" },
        { label: "410", value: "410" },
    ]);

    const [filters, setFilters] = useState({
        search: '',
        status: ''
    });
    const [formModalOpen, setFormModalOpen] = useState(false);



    useEffect(() => {
        fetchData();
    }, [projectId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [urlsResponse, domainResponse] = await Promise.all([
                projectRedirectionUrlsService.getProjectRedirectionUrls({ project_id: projectId, filters: filters }),
                projectsService.getProjectDomain(projectId)
            ]);

            if (urlsResponse && urlsResponse.status) {
                setUrls(urlsResponse.data?.urls || []);
                const existingStatusCodes = urlsResponse.data?.urls.map(url => url.status_code);
                const newStatusCodes = existingStatusCodes.filter(code =>
                    !statusCodeOptions.some(option => option.value === code)
                );
                if (newStatusCodes.length > 0) {
                    const newOptions = newStatusCodes.map(code => ({
                        label: code,
                        value: code
                    }));
                    setStatusCodeOptions(prevOptions => [...prevOptions, ...newOptions]);
                }
            }


            if (domainResponse && domainResponse.status) {
                const projectDomain = domainResponse.data;
                if (!projectDomain.public_host.startsWith('https://')) {
                    setDomain("https://" + projectDomain.public_host);
                } else {
                    setDomain(projectDomain.public_host);
                }
            }
        } catch (error) {
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            centered
            className="custom-modal"
        >
            <ModalHeader>
                {title}
            </ModalHeader>
            <ModalBody className="text-center p-4">
                <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    size="3x"
                    color={colors.error}
                    className="mb-3"
                />
                <p className="text-muted">{message}</p>
            </ModalBody>
            <ModalFooter className="justify-content-center border-top-0">
                <Button
                    color="danger"
                    onClick={onConfirm}
                    className="px-4 hover:opacity-90"
                >
                    Confirm
                </Button>{" "}
                <Button
                    color="secondary"
                    onClick={onClose}
                    className="px-4 hover:opacity-90"
                >
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUrl({ ...url, [name]: value.trim() });
    };

    const handleStatusCodeChange = (selectedOption) => {
        const newStatusCode = selectedOption ? selectedOption.value : "";
        const newStatusCodeDesc = selectedOption ? selectedOption.label.split(" - ")[1] : "";

        setUrl({
            ...url,
            status_code: newStatusCode,
            status_code_desc: newStatusCodeDesc,
        });

        if (selectedOption && selectedOption.__isNew__) {
            const newOption = {
                label: selectedOption.label,
                value: selectedOption.value,
            };
            setStatusCodeOptions((prevOptions) => [...prevOptions, newOption]);
        }
    };

    const resetForm = () => {
        setUrl({
            source_url: "",
            destination_url: "",
            status_code: "",
        });
        setIsEdit(false);
        setEditIndex(null);
    };

    const resetSearchForm = () => {
        setFilters({
            search: '',
            status: ''
        });
        fetchData(); // reloads with no filters
    };

    const handleSearchForm = (e) => {
        e.preventDefault();
        fetchData();
    };

    const openAddModal = () => {
        resetForm();
        setFormModalOpen(true);
    };

    const openEditModal = (index) => {
        setUrl(urls[index]);
        setIsEdit(true);
        setEditIndex(index);
        setFormModalOpen(true);
    };

    const handleEdit = (index) => {
        setUrl(urls[index]);
        setIsEdit(true);
        setEditIndex(index);
    };

    const handleDeleteConfirmation = (index) => {
        setDeleteModal(true);
        setToDeleteIndex(index);
    };

    const saveData = async (docData) => {
        try {
            const response = await projectRedirectionUrlsService.saveProjectRedirectionUrls({
                project_id: projectId,
                ...docData,
            });

            console.log(response);
            if (response && response.status) {
                setUrls(response.data?.urls || []);
                toast.success(`${docData.action ? `${docData.action.charAt(0).toUpperCase() + docData.action.slice(1)}` : 'Action'} successful!`);

            } else {
                toast.error(response?.error?.message || "Failed to save data");
            }
            setFormModalOpen(false); 
            resetForm();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = () => {
        saveData({
            action: "delete",
            toDeleteIndex: toDeleteIndex,
            url: urls[toDeleteIndex]
        });
        setDeleteModal(false);
        setToDeleteIndex(null);
    };
    const validateStatusCode = (code) => {
        return /^[0-9]{3}$/.test(code);
    };

    const isValidUrl = async (url) => {
        try {
            new URL(url);
            return true;
        } catch (e) {
            console.error("Invalid URL:", e);
            return false;
        }
    };

    const handleSubmit = async () => {
        if (!url.source_url || !url.destination_url || !url.status_code) {
            toast.error("Please fill all the required fields");
            return;
        }

        const fullSourceUrl = `${domain}${url.source_url}`;
        if (fullSourceUrl === url.destination_url) {
            toast.error("Source URL and Destination URL must not be the same!");
            return;
        }

        if (!url.source_url.startsWith("/")) {
            toast.error("Source URL must start with a '/'");
            return;
        }

        const isDuplicate = urls.some(
            (data, index) => data.source_url === url.source_url && index !== editIndex
        );

        if (isDuplicate) {
            toast.error("Source URL must be unique!");
            return;
        }


        if (!validateStatusCode(url.status_code)) {
            toast.error("Status code must be a valid 3-digit number!");
            return;
        }


        const isDestinationValid = await isValidUrl(url.destination_url);
        if (!isDestinationValid) {
            toast.error("Destination URL is not valid!");
            return;
        }

        const action = isEdit && editIndex !== null
            ? { action: "edit", editIndex: editIndex, url: url }
            : { action: "save", url: url };

        saveData(action);
    }

    return (
        <div className="content mt-4" style={{ backgroundColor: colors.background }}>
            <Container>
            <Card className="shadow-sm" style={{ backgroundColor: colors.cardBackground, borderRadius: '10px', }}>
                        <CardHeader
                            className="text-left font-semibold py-3"
                            tag='h2'
                            style={{
                                bottom: '-10px',
                                color: 'black',
                                borderBottom: '3px solid',
                                position: 'relative',
                            }}
                        >
                            Redirection URLs
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '15px',
                                    right: '10px',
                                    fontSize: '0.8rem',
                                    color: '#555',
                                }}
                            >
                                Domain: {domain}
                            </div>
                        </CardHeader>
                    <CardBody>
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner color="info" />
                                <p>Loading Redirection URLs...</p>
                            </div>
                        ) : (
                            <>
                                    <Form className="mb-3" onSubmit={handleSearchForm}>
                                        <Row>
                                            {/* Search Field */}
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>Search</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="Search by Source or Destination URL"
                                                        value={filters.search}
                                                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                                    />
                                                </FormGroup>
                                            </Col>

                                            {/* Status Code Filter */}
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label>Status Code</Label>
                                                    <ReactSelect
                                                        value={filters.status ? { value: filters.status, label: filters.status } : null}
                                                        onChange={(selected) =>
                                                            setFilters({ ...filters, status: selected ? selected.value : '' })
                                                        }
                                                        options={[
                                                            { value: '', label: 'All' },
                                                            ...statusCodeOptions
                                                        ]}
                                                        isClearable
                                                        placeholder="Select status"
                                                    />
                                                </FormGroup>
                                            </Col>

                                            {/* Search Button */}
                                            <Col md={5} className="d-flex align-items-end">
                                                <Button 
                                                    color="info" 
                                                    type="submit"
                                                    style={{ cursor: "pointer", marginRight: "10px" }}
                                                    title="Search"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faSearch}
                                                        style={{ fontSize: '1rem' }} />
                                                </Button>
                                                <Button
                                                    color="secondary"
                                                    onClick={resetSearchForm}
                                                    style={{ cursor: "pointer", marginRight: "10px" }}
                                                    title="Reset"
                                                >
                                                    <FontAwesomeIcon icon={faUndo}
                                                        style={{ fontSize: '1rem' }} />
                                                </Button>
                                                <Button color="primary" onClick={openAddModal} style={{ cursor: "pointer", marginRight: "10px" }}>
                                                    <FontAwesomeIcon icon={faPlusCircle} style={{ fontSize: '1rem' }} /> Add URL
                                                </Button>
                                            </Col>
                                            {/* <Col md="3" className="d-flex align-items-end">
                                                
                                            </Col> */}
                                        </Row>
                                    </Form>

                                <Table
                                    striped
                                    hover
                                    responsive
                                    className="mt-4"
                                >
                                    <thead style={{ backgroundColor: colors.background }}>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Source URL</th>
                                            <th>Destination URL</th>
                                            <th>Status Code</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {urls.length > 0 ? (
                                            urls.map((url, index) =>
                                                index !== editIndex && (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{url.source_url}</td>
                                                        <td>{url.destination_url}</td>
                                                        <td>{url.status_code}</td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <Button
                                                                    color="info"
                                                                    onClick={() => openEditModal(index)}
                                                                    title="Edit"
                                                                    style={{ cursor: "pointer", marginRight: "20px" }}                                                                >
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </Button>
                                                                <Button
                                                                    color="danger"
                                                                    title="Delete"
                                                                    onClick={() => handleDeleteConfirmation(index)}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        ) : (
                                            <tr>
                                                <td colSpan='5' className='text-center text-muted'>
                                                    No Redirection URLs found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </CardBody>
                </Card>
                <ConfirmationModal
                    isOpen={deleteModal}
                    onClose={() => setDeleteModal(false)}
                    onConfirm={handleDelete}
                    title="Confirm Deletion"
                    message="Are you sure you want to delete this URL?"
                />
                <Modal
                    isOpen={formModalOpen}
                    toggle={() => {
                        setFormModalOpen(false); 
                        resetForm();
                    }}
                    size="lg"
                    centered
                    style={{
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)"
                    }}
                >
                    <ModalHeader
                        style={{
                            borderBottom: "none",
                            fontWeight: 600,
                            fontSize: "1.25rem",
                            padding: "1rem 1.5rem",
                            backgroundColor: "#f8f9fa"
                        }}
                    >
                        <FontAwesomeIcon
                            icon={isEdit ? faEdit : faPlusCircle}
                            style={{ marginRight: "8px", color: "#17a2b8" }}
                        />
                        {isEdit ? "Edit Redirection URL" : "Add Redirection URL"}
                    </ModalHeader>

                    <ModalBody style={{ padding: "1.5rem" }}>
                        <Form>
                            <Row style={{ rowGap: "20px" }}>
                                <Col md={8}>
                                    <FormGroup>
                                        <Label style={{ fontWeight: 600 }}>
                                            Source URL <span style={{ color: "red" }}>*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            name="source_url"
                                            value={url.source_url}
                                            onChange={handleInputChange}
                                            placeholder="Enter Source URL"
                                        />
                                        {url.source_url && (
                                            <div style={{ marginTop: "5px", fontSize: "0.8rem", color: "#6c757d" }}>
                                                <span
                                                    style={{
                                                        background: "#f1f3f5",
                                                        padding: "3px 8px",
                                                        borderRadius: "6px"
                                                    }}
                                                >
                                                    {domain}{url.source_url}
                                                </span>
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>

                                <Col md={4}>
                                    <FormGroup>
                                        <Label style={{ fontWeight: 600 }}>
                                            Status Code <span style={{ color: "red" }}>*</span>
                                        </Label>
                                        <CreatableSelect
                                            isMulti={false}
                                            options={statusCodeOptions}
                                            value={
                                                url.status_code
                                                    ? statusCodeOptions.find(option => option.value === url.status_code)
                                                    : null
                                            }
                                            onChange={handleStatusCodeChange}
                                            formatCreateLabel={(inputValue) => `Add code: "${inputValue}"`}
                                            placeholder="Select Code"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={8}>
                                    <FormGroup>
                                        <Label style={{ fontWeight: 600 }}>
                                            Destination URL <span style={{ color: "red" }}>*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            name="destination_url"
                                            value={url.destination_url}
                                            onChange={handleInputChange}
                                            placeholder="Enter Destination URL"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </ModalBody>

                    <ModalFooter
                        style={{
                            justifyContent: "center",
                            borderTop: "none",
                            padding: "1rem"
                        }}
                    >
                        <Button
                            color="info"
                            onClick={handleSubmit}
                            style={{ padding: "8px 20px", borderRadius: "6px" }}
                            className="hover:opacity-90"
                        >
                            <FontAwesomeIcon icon={isEdit ? faEdit : faPlusCircle} /> {isEdit ? "Update" : "Add"}
                        </Button>
                        <Button
                            color="secondary"
                            onClick={() => {
                                setFormModalOpen(false); 
                                resetForm()
                            }}
                            style={{ padding: "8px 20px", borderRadius: "6px" }}
                            className="hover:opacity-90"
                        >
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
                <ToastContainer
                    autoClose={2000}
                    position='bottom-right'
                    newestOnTop
                    theme="colored"
                />
            </Container>
        </div>
    );
}

export default RedirectionUrls;